import { vehicleLateralBalance } from '../../lib/lateralBalance'

export default function LateralBalanceSection({ analysis, centerOfGravity, loadSpace, isDark }: { analysis: any; centerOfGravity?: any; loadSpace?: any; isDark?: boolean }) {
  const lateral = vehicleLateralBalance(analysis?.validation?.lateralValidation, centerOfGravity, loadSpace)
  if (!lateral) return <p className="text-sm text-slate-500">Équilibre latéral non vérifié : dimensions ou centre de gravité indisponibles.</p>

  const fmtPct = (value: any) => (Number.isFinite(value) ? `${value.toFixed(1)} %` : '—')
  const fmtPts = (value: any) => (Number.isFinite(value) ? value.toFixed(1) : '—')

  const left = Number.isFinite(lateral.leftPercentage) ? lateral.leftPercentage : 50
  const right = Number.isFinite(lateral.rightPercentage) ? lateral.rightPercentage : 50
  const threshold = Number.isFinite(lateral.maxImbalance) ? lateral.maxImbalance : null
  const status: 'over' | 'ok' | 'unknown' =
    lateral.isBalanced === false ? 'over' : lateral.isBalanced === true ? 'ok' : 'unknown'

  const palette = {
    over: {
      ink: isDark ? 'text-amber-400' : 'text-amber-600',
      fill: isDark ? 'bg-amber-500' : 'bg-amber-500',
      ring: isDark ? 'ring-amber-500/40' : 'ring-amber-500/30',
      badgeBg: isDark ? 'bg-amber-500/15' : 'bg-amber-50',
    },
    ok: {
      ink: isDark ? 'text-emerald-400' : 'text-emerald-600',
      fill: isDark ? 'bg-emerald-500' : 'bg-emerald-500',
      ring: isDark ? 'ring-emerald-500/40' : 'ring-emerald-500/30',
      badgeBg: isDark ? 'bg-emerald-500/15' : 'bg-emerald-50',
    },
    unknown: {
      ink: isDark ? 'text-slate-400' : 'text-slate-500',
      fill: isDark ? 'bg-slate-500' : 'bg-slate-400',
      ring: isDark ? 'ring-slate-500/30' : 'ring-slate-400/20',
      badgeBg: isDark ? 'bg-slate-500/10' : 'bg-slate-100',
    },
  }[status]

  const trackBg = isDark ? 'bg-slate-800' : 'bg-slate-200'
  const cardBorder = isDark ? 'border-slate-700' : 'border-slate-200'
  const cardBg = isDark ? 'bg-slate-900' : 'bg-white'
  const textMuted = isDark ? 'text-slate-400' : 'text-slate-500'
  const textBody = isDark ? 'text-slate-200' : 'text-slate-700'

  // Tolerance band centered on the 50/50 axis, width in percentage points.
  const bandHalf = threshold != null ? threshold / 2 : 0
  const bandLeft = Math.max(0, 50 - bandHalf)
  const bandWidth = Math.min(100, bandHalf * 2)

  const statusIcon = {
    over: (
      <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden="true">
        <path d="M10 3.3 2 17h16L10 3.3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M10 8.2v3.6M10 14.2v.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    ok: (
      <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden="true">
        <path d="M4 10.5 8 14l8-8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    unknown: (
      <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden="true">
        <circle cx="10" cy="10" r="7.3" stroke="currentColor" strokeWidth="1.5" />
        <path d="M7.8 8a2.2 2.2 0 1 1 3.1 2c-.7.5-.9.9-.9 1.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="10" cy="13.6" r="0.15" fill="currentColor" stroke="currentColor" strokeWidth="1.1" />
      </svg>
    ),
  }[status]

  const statusLabel = { over: 'À rééquilibrer', ok: 'Dans le seuil', unknown: 'Non vérifié' }[status]

  return (
    <section className={`rounded-xl border ${cardBorder} ${cardBg} p-4`}>
      <div className="flex items-center justify-between gap-3">
        <h3 className={`text-sm font-semibold ${textBody}`}>Équilibre latéral du chargement</h3>
        <span className={`inline-flex items-center gap-1.5 rounded-full ${palette.badgeBg} ${palette.ink} px-2.5 py-1 text-xs font-medium`}>
          {statusIcon}
          {statusLabel}
        </span>
      </div>

      <div
        className="mt-4"
        role="img"
        aria-label={`Charge répartie à ${fmtPct(left)} à gauche et ${fmtPct(right)} à droite, écart de ${fmtPts(lateral.imbalance)} points pour un seuil de ${threshold ?? 'non défini'} points`}
      >
        <div className={`relative h-3 w-full overflow-hidden rounded-full ${trackBg}`}>
          {threshold != null && (
            <div
              className={`absolute inset-y-0 ${isDark ? 'bg-slate-600/40' : 'bg-slate-400/25'}`}
              style={{ left: `${bandLeft}%`, width: `${bandWidth}%` }}
            />
          )}
          <div className={`absolute inset-y-0 left-0 ${palette.fill} opacity-80`} style={{ width: `${left}%` }} />
          <div className={`absolute inset-y-0 ${isDark ? 'bg-slate-500' : 'bg-slate-400'}`} style={{ left: '50%', width: '1.5px' }} />
          <div
            className={`absolute top-1/2 h-3.5 w-3.5 -translate-y-1/2 -translate-x-1/2 rounded-full border-2 ${isDark ? 'border-slate-900' : 'border-white'} ${palette.fill} ring-2 ${palette.ring}`}
            style={{ left: `${left}%` }}
          />
        </div>
        <div className={`mt-1.5 flex justify-between text-xs ${textMuted}`}>
          <span>Gauche · {fmtPct(left)}</span>
          <span>Droite · {fmtPct(right)}</span>
        </div>
      </div>

      <div className={`mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm ${textBody}`}>
        <span>
          Écart gauche/droite&nbsp;:{' '}
          <span className="font-semibold">{fmtPts(lateral.imbalance)} pts</span>
        </span>
        <span className={textMuted}>
          Seuil du modèle&nbsp;: {threshold ?? '—'} pts
        </span>
      </div>

      <p className={`mt-2 text-xs ${textMuted}`}>
        Gauche et droite sont définies en regardant vers l’avant du véhicule : Y=0 à droite, Y maximal à gauche.
        Répartition estimée à partir du centre de gravité du fret, distincte d’une pesée des roues.
        Le seuil affiché est celui du modèle, pas une limite réglementaire universelle.
      </p>
    </section>
  )
}
